// Extend existing analytics with consented, namespaced Shopify custom events.
// Native checkout/purchase events remain owned by the installed channel pixels.
(() => {
  const allowed=new Set(['landing','builder_open','first_design_upload','first_design_library','layout_ready','cart_added','checkout_clicked','cart_view','warning_open','warning_present','warning_cleared','warning_reason','search_empty','search_found','edit_started','edit_saved','add_to_sheet','add_to_cart','design_view','category_view','size_selected','quantity_changed','search_submitted','search_results','upload_completed','layout_completed','cart_add_succeeded','cart_add_failed','warning_resolved','edit_cancelled','draft_restored','stage_error']);
  const warningCodes=new Set(['low_resolution','solid_background','semi_transparency','auto_crop','preview_unavailable']);
  const seen=new Set();
  const clarityTags=new Set();
  const flowFor={public_catalog:'transfers',pdp:'transfers',builder_library:'library',own_upload:'upload',finished_sheet:'sheet',sample:'sample'};
  const flowSteps=new Set(['category_view','design_view','cart_view','add_to_sheet','upload_completed','layout_completed','cart_add_succeeded','checkout_clicked']);
  const eventKey='bdtf_completed_operations_v1';
  const aliases={layout_ready:'layout_completed',cart_added:'cart_add_succeeded',add_to_cart:'cart_add_succeeded',warning_cleared:'warning_resolved'};
  const attributionKey='bdtf_catalog_attribution_v1';
  function attribution(){
    // Store a first-touch campaign only after analytics permission. Do not retain
    // full referrer URLs, arbitrary query strings, search terms or click IDs.
    try{
      const previous=JSON.parse(window.sessionStorage?.getItem(attributionKey)||'null');if(previous)return previous;
      const url=new URL(window.location.href),out={};
      for(const key of ['utm_source','utm_medium','utm_campaign','utm_content']){
        const value=url.searchParams.get(key)?.trim();
        // Campaign labels can contain spaces/dots, but not addresses, full URLs,
        // control characters or arbitrary query payloads.
        if(value&&/^[\p{L}\p{N} _.\-]{1,80}$/u.test(value)&&!/(?:https?\b|www\.|\b\d{7,}\b)/i.test(value))out[key]=value;
      }
      const referrer=document.referrer?new URL(document.referrer):null;
      if(referrer&&referrer.origin!==url.origin)out.referrer_host=referrer.hostname;
      if(Object.keys(out).length)window.sessionStorage?.setItem(attributionKey,JSON.stringify(out));return out;
    }catch{return {};}
  }
  function detailPayload(name,detail){
    const out={viewport_class:matchMedia('(max-width: 749px)').matches?'small':matchMedia('(max-width: 1023px)').matches?'medium':'large',...attribution()};
    out.environment=document.body.dataset?.analyticsEnvironment==='preview'?'preview':'storefront';
    if(/^d_[a-f0-9]{32}$/.test(detail.design_id||''))out.design_id=detail.design_id;
    if(['pocket','youth','adult','custom'].includes(detail.preset))out.preset=detail.preset;
    if(['public_catalog','pdp','builder_library','own_upload','finished_sheet','sample'].includes(detail.source))out.source=detail.source;
    for(const key of ['quantity','width_in','height_in'])if(Number.isFinite(detail[key])&&detail[key]>0&&detail[key]<=10000)out[key]=detail[key];
    if(/^[a-z0-9-]{1,80}$/.test(detail.category||''))out.category=detail.category;
    // Only a confirmed standalone cart mutation carries exact value. Sheet
    // estimates are never forwarded as revenue or native add_to_cart events.
    if(name==='cart_add_succeeded'&&detail.currency==='USD'&&Number.isFinite(detail.value)&&detail.value>0){out.currency='USD';out.value=detail.value;}
    if(/^[a-f0-9-]{36}$/.test(detail.operation_id||''))out.operation_id=detail.operation_id;
    if(Number.isSafeInteger(detail.layout_revision)&&detail.layout_revision>=0)out.layout_revision=detail.layout_revision;
    if(Number.isSafeInteger(detail.result_count)&&detail.result_count>=0)out.result_count=Math.min(detail.result_count,100000);
    if(/^[a-z][a-z0-9_]{0,59}$/.test(detail.error_code||''))out.error_code=detail.error_code;
    if(['upload','layout','cart','restore','preview'].includes(detail.stage))out.stage=detail.stage;
    if(name==='warning_reason'){
      if(warningCodes.has(detail.warning_code))out.warning_code=detail.warning_code;
      if(['present','opened'].includes(detail.warning_action))out.warning_action=detail.warning_action;
    }
    return out;
  }
  function emit(name,detail={}){
    const {once=false}=detail;
    if(!allowed.has(name))return false;
    name=aliases[name]||name;
    const key=[name,detail.source||'',detail.design_id||'',detail.category||'',detail.layout_revision??'',detail.warning_code||'',detail.warning_action||''].join(':');
    if(once&&seen.has(key))return false;
    const privacy=window.Shopify?.customerPrivacy;
    let consent=false;try{consent=privacy?.analyticsProcessingAllowed()===true;}catch{}
    if(!consent){try{window.sessionStorage?.removeItem(attributionKey);}catch{}clarityTags.clear();return false;}
    const payload=detailPayload(name,detail);let sent=false;
    if(name==='warning_reason'&&(!payload.warning_code||!payload.warning_action))return false;
    const completed=['cart_add_succeeded','edit_saved'].includes(name)&&payload.operation_id;
    let previous=[];try{previous=JSON.parse(window.sessionStorage?.getItem(eventKey)||'[]');}catch{}
    if(completed&&previous.includes(payload.operation_id))return false;
    if(typeof window.clarity==='function'){try{
      // Clarity tags describe session cohorts, not per-event properties. A
      // separate source-qualified milestone preserves the actual flow context.
      for(const field of ['viewport_class','environment','source','utm_source','utm_medium','utm_campaign','utm_content','referrer_host']){
        const value=payload[field];if(!value)continue;
        const tag=field+':'+value;if(clarityTags.has(tag))continue;
        window.clarity('set','bdtf_'+field,value);clarityTags.add(tag);
      }
      const event=name==='warning_reason'?'bdtf_warning_'+payload.warning_code+'_'+payload.warning_action:'bdtf_'+name+'_'+payload.viewport_class;
      window.clarity('event',event);sent=true;
      const flow=flowFor[payload.source];
      if(flow&&flowSteps.has(name))window.clarity('event','bdtf_flow_'+flow+'_'+name);
    }catch{}}
    if(typeof window.Shopify.analytics?.publish==='function'){
      try{Promise.resolve(window.Shopify.analytics.publish('bdtf:'+name,payload)).catch(()=>{});sent=true;}catch{}
    }
    if(!sent)return false;
    if(once)seen.add(key);
    if(completed){try{window.sessionStorage?.setItem(eventKey,JSON.stringify([...previous,payload.operation_id].slice(-200)));}catch{}}
    return true;
  }
  window.BDTFAnalytics={emit};
  const start=()=>{if(document.body.classList.contains('page--index'))emit('landing',{once:true});if(document.body.classList.contains('is-builder'))emit('builder_open',{once:true});const category=document.querySelector?.('[data-transfer-category]')?.dataset.transferCategory;if(category)emit('category_view',{once:true,category,source:'public_catalog'});if(document.querySelector?.('[data-sample-product]'))emit('design_view',{once:true,source:'sample'});};
  document.addEventListener('visitorConsentCollected',start);
  if(window.Shopify?.loadFeatures)window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],error=>{if(!error)start();});
  else start();
})();
